import React, { useState } from 'react';

/**
 * RiskMatrix Component
 * Displays risk categories heat map and Monte Carlo simulation results
 */
function RiskMatrix({
  categories,
  overallRiskScore,
  monteCarlo,
  monteCarloMeta,
  monteCarloMissingHint,
  monteCarloSecondary,
  monteCarloSecondaryTitle,
  metricTooltips = {},
}) {
  const [showMonteCarloDetails, setShowMonteCarloDetails] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  
  if (!categories) return null;

  const levels = ['L', 'M', 'H', 'C', 'S']; // Shortened labels
  const levelLabels = ['Low', 'Moderate', 'High', 'Critical', 'Severe'];
  const totalScore = overallRiskScore != null ? overallRiskScore : categories.reduce((sum, cat) => sum + (cat?.score ?? 0), 0);
  
  // Calculate average risk score (avoid 0/0 when categories is empty)
  const avgScore =
    categories.length > 0 ? totalScore / categories.length : null;
  
  // Get color for score
  const getScoreColor = (score) => {
    const colors = ['bg-green-400', 'bg-green-300', 'bg-yellow-400', 'bg-orange-400', 'bg-red-500'];
    return colors[score - 1] || 'bg-gray-300';
  };
  
  return (
    <div className="bg-gray-50 rounded-lg p-3 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-bold text-gray-900 text-sm">Risk Matrix</h4>
        <div className={`text-xs font-semibold px-2 py-0.5 rounded ${
          avgScore == null || !Number.isFinite(avgScore) ? 'bg-gray-100 text-gray-700' :
          avgScore <= 2 ? 'bg-green-100 text-green-800' :
          avgScore <= 3 ? 'bg-yellow-100 text-yellow-800' :
          avgScore <= 4 ? 'bg-orange-100 text-orange-800' :
          'bg-red-100 text-red-800'
        }`}>
          {avgScore != null && Number.isFinite(avgScore) ? `${avgScore.toFixed(1)}/5` : '—/5'}
        </div>
      </div>
      
      {/* Compact Risk Grid */}
      <div className="space-y-1">
        {/* Legend */}
        <div className="flex gap-1 text-[10px] text-gray-500">
          <div className="w-16"></div>
          {levels.map((l, i) => (
            <div key={l} className="flex-1 text-center" title={levelLabels[i]}>{l}</div>
          ))}
        </div>
        
        {/* Risk Categories */}
        {categories.filter(Boolean).map((cat, idx) => (
          <div key={cat?.name ?? idx} className="flex gap-1 items-center">
            <div className="w-16 text-[10px] font-medium text-gray-700 truncate" title={cat?.name}>
              {cat?.name}
            </div>
            <div className="flex-1 flex gap-1">
              {[1, 2, 3, 4, 5].map((score) => {
                const isActive = cat?.score === score;
                return (
                  <div 
                    key={score}
                    className={`flex-1 h-4 rounded-sm ${isActive ? getScoreColor(score) : 'bg-gray-200'}`}
                    title={`${cat?.name ?? 'Risk'}: ${levelLabels[score - 1]} (${score})`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-2 text-[10px] text-gray-500 text-center">
        Total: {totalScore}/30
      </div>

      {!monteCarlo && monteCarloMissingHint && (
        <p
          className="mt-2 text-[10px] text-amber-900 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 leading-snug"
          role="status"
        >
          {monteCarloMissingHint}
        </p>
      )}

      {/* Monte Carlo Summary - Compact */}
      {monteCarlo && (
        <div className="mt-3 pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold text-gray-700">
              Monte Carlo ({monteCarlo.iterations?.toLocaleString()} runs)
              {monteCarloMeta?.metric_display_name && (
                <span className="block font-normal text-gray-500 mt-0.5">
                  {monteCarloMeta.metric_display_name}
                  {monteCarloMeta.realism_level != null && (
                    <span className="ml-1">· L{monteCarloMeta.realism_level}</span>
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {monteCarloMeta?.assumptions && (
                <button
                  type="button"
                  onClick={() => setShowAssumptions(!showAssumptions)}
                  className="text-[10px] text-gray-600 hover:text-gray-800"
                >
                  {showAssumptions ? 'Hide' : 'Assumptions'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowMonteCarloDetails(!showMonteCarloDetails)}
                className="text-[10px] text-blue-600 hover:text-blue-800"
              >
                {showMonteCarloDetails ? 'Less' : 'More'}
              </button>
            </div>
          </div>
          {showAssumptions && monteCarloMeta?.assumptions && (
            <div className="mb-2 p-2 bg-white rounded border border-gray-100 text-[10px] text-gray-600 space-y-1 max-h-28 overflow-y-auto">
              {(monteCarloMeta.assumptions.notes || []).map((n, i) => (
                <p key={`n-${i}`}>{n}</p>
              ))}
              {(monteCarloMeta.assumptions.detail_lines || []).map((line, i) => (
                <p key={`d-${i}`} className="text-gray-500">· {line}</p>
              ))}
            </div>
          )}
          
          {/* Summary Stats - Compact */}
          <div className="flex gap-2">
            <div className="flex-1 bg-white rounded p-1.5 text-center">
              <div className="text-sm font-bold text-primary-600">
                {monteCarlo.mean_return != null ? `${Number(monteCarlo.mean_return).toFixed(1)}%` : '—'}
              </div>
              <div className="text-[10px] text-gray-500" title={metricTooltips.mcMean || undefined}>
                Mean
              </div>
            </div>
            <div className="flex-1 bg-white rounded p-1.5 text-center">
              <div className="text-sm font-bold text-green-600">
                {monteCarlo.probability_of_target_return != null ? `${(monteCarlo.probability_of_target_return * 100).toFixed(0)}%` : '—'}
              </div>
              <div
                className="text-[10px] text-gray-500"
                title={metricTooltips.mcProb || 'Versus strategy-specific hurdle'}
              >
                P(&gt;{monteCarloMeta?.assumptions?.target_return_threshold_pct ?? 8}%)
              </div>
            </div>
          </div>

          {monteCarloSecondary && (
            <div className="mt-2 pt-2 border-t border-dashed border-gray-300">
              <div className="text-[10px] font-semibold text-gray-700 mb-1" title={metricTooltips.mcSecondary || undefined}>
                {monteCarloSecondaryTitle || 'Year 1 cash-on-cash (engine, pre-tax %)'}
              </div>
              <div className="flex gap-2">
                <div className="flex-1 bg-white rounded p-1.5 text-center border border-indigo-100">
                  <div className="text-sm font-bold text-indigo-700">
                    {monteCarloSecondary.mean_return != null
                      ? `${Number(monteCarloSecondary.mean_return).toFixed(1)}%`
                      : '—'}
                  </div>
                  <div className="text-[10px] text-gray-500">Mean</div>
                </div>
                <div className="flex-1 bg-white rounded p-1.5 text-center border border-indigo-100">
                  <div className="text-sm font-bold text-gray-800">
                    {monteCarloSecondary.percentile_5 != null
                      ? `${Number(monteCarloSecondary.percentile_5).toFixed(1)}%`
                      : '—'}
                  </div>
                  <div className="text-[10px] text-gray-500">5th pct</div>
                </div>
              </div>
              <p className="text-[9px] text-gray-400 mt-1 leading-snug">
                From strategy_cashflow engine with jittered inputs (parallel to the 5y path model above).
              </p>
            </div>
          )}

          {/* Detailed Monte Carlo Stats - Compact */}
          {showMonteCarloDetails && (
            <div className="bg-white rounded p-2 mt-2 space-y-2">
              {/* Percentile Distribution - Compact */}
              <div className="space-y-1">
                {(() => {
                  const rows = [
                    { label: '95th', value: monteCarlo.percentile_95, color: 'bg-green-500' },
                    { label: '75th', value: monteCarlo.percentile_75, color: 'bg-blue-400' },
                    { label: '50th', value: monteCarlo.percentile_50, color: 'bg-blue-600' },
                    { label: '25th', value: monteCarlo.percentile_25, color: 'bg-orange-400' },
                    { label: '5th', value: monteCarlo.percentile_5, color: 'bg-red-500' },
                  ];
                  const vals = rows
                    .map((r) => r.value)
                    .filter((v) => v != null && Number.isFinite(Number(v)))
                    .map(Number);
                  const pMin = vals.length ? Math.min(...vals) : 0;
                  const pMax = vals.length ? Math.max(...vals) : 1;
                  const span = pMax - pMin || 1;
                  return rows.map((p) => {
                    const v = p.value;
                    const barPct =
                      v != null && Number.isFinite(Number(v))
                        ? Math.min(100, Math.max(0, ((Number(v) - pMin) / span) * 100))
                        : 0;
                    return (
                      <div key={p.label} className="flex items-center gap-2 text-[10px]">
                        <span className="w-8 text-gray-500">{p.label}:</span>
                        <div
                          className="flex-1 bg-gray-200 rounded-full h-1.5"
                          title="Bar shows relative position between displayed percentiles (not absolute scale)."
                        >
                          <div
                            className={`${p.color} h-1.5 rounded-full`}
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                        <span className="w-10 text-right font-semibold">
                          {v != null ? `${Number(v).toFixed(1)}%` : '—'}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Risk Metrics & Scenarios - Compact Grid */}
              <div className="grid grid-cols-4 gap-1 pt-1 border-t border-gray-200 text-[10px]">
                <div className="text-center">
                  <div className="text-gray-500">StdDev</div>
                  <div className="font-semibold">
                    {monteCarlo.std_deviation != null ? `±${Number(monteCarlo.std_deviation).toFixed(1)}%` : '—'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-500">R/R</div>
                  <div className={`font-semibold ${
                    (monteCarlo.mean_return / monteCarlo.std_deviation) > 1 ? 'text-green-600' :
                    (monteCarlo.mean_return / monteCarlo.std_deviation) > 0.5 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {monteCarlo.mean_return != null && monteCarlo.std_deviation > 0 
                      ? (monteCarlo.mean_return / monteCarlo.std_deviation).toFixed(1)
                      : '—'}
                  </div>
                </div>
                <div className="text-center bg-red-50 rounded">
                  <div className="text-red-600">Worst</div>
                  <div className="font-bold text-red-700">
                    {monteCarlo.worst_case_scenario != null ? `${Number(monteCarlo.worst_case_scenario).toFixed(0)}%` : '—'}
                  </div>
                </div>
                <div className="text-center bg-green-50 rounded">
                  <div className="text-green-600">Best</div>
                  <div className="font-bold text-green-700">
                    {monteCarlo.best_case_scenario != null ? `${Number(monteCarlo.best_case_scenario).toFixed(0)}%` : '—'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RiskMatrix;
